'use client';
import React, { useState } from 'react';
import { cn } from '../../lib/utils';

/**
 * BorderButton — capsule outline button with animated dot.
 * Props:
 *  - children: button label (defaults to "Get Started")
 *  - onClick: click handler
 *  - dotColor: tailwind color of the dot (e.g. 'bg-red-500')
 *  - dotHoverColor: tailwind color on hover
 *  - className: extra classes for sizing/spacing
 */
export default function BorderButton({
  children = 'Get Started',
  onClick,
  dotColor = 'bg-red-500',
  dotHoverColor = 'bg-red-400',
  className = '',
}) {
  const [isHovered, setIsHovered] = useState(false);
  const [isPressed, setIsPressed] = useState(false);

  return (
    <button
      onClick={onClick}
      className={cn(
        'group relative border-2 inline-flex justify-center items-center gap-3 border-white/70 rounded-full px-7 h-12',
        'transition-all duration-500 ease-out hover:border-white hover:shadow-lg hover:shadow-white/20',
        'hover:scale-[1.04] active:scale-95 overflow-hidden backdrop-blur-sm',
        'before:absolute before:inset-0 before:bg-gradient-to-r before:from-transparent',
        'before:via-white/5 before:to-transparent before:translate-x-[-100%]',
        'hover:before:translate-x-[100%] before:transition-transform before:duration-700',
        className
      )}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => {
        setIsHovered(false);
        setIsPressed(false);
      }}
      onMouseDown={() => setIsPressed(true)}
      onMouseUp={() => setIsPressed(false)}
    >
      {/* Subtle red glow on hover */}
      <div className="absolute inset-0 rounded-full bg-gradient-to-r from-red-500/0 via-red-500/10 to-red-500/0 opacity-0 group-hover:opacity-100 transition-opacity duration-500" />

      {/* Text */}
      <span className="text-white font-medium tracking-[0.25em] text-[13px] uppercase transition-all duration-300 group-hover:text-white relative z-10">
        {children}
      </span>

      {/* Animated dot */}
      <span
        className={cn(
          'relative z-10 w-3 h-3 rounded-full transition-all duration-500 ease-out',
          dotColor,
          isHovered ? `${dotHoverColor} shadow-lg shadow-red-500/50 scale-110` : '',
          isPressed ? 'scale-90' : '',
          'before:absolute before:inset-0 before:bg-red-400 before:rounded-full before:animate-pulse before:opacity-0 group-hover:before:opacity-30'
        )}
      >
        {/* Ripple effect */}
        <span
          className="absolute inset-0 rounded-full bg-red-500 animate-ping opacity-0 group-hover:opacity-75"
          style={{ animationDuration: '2s' }}
        />
      </span>

      {/* Hover border pulse */}
      <div className="absolute inset-0 rounded-full border-2 border-red-500/0 group-hover:border-red-500/30 transition-all duration-500 animate-pulse opacity-0 group-hover:opacity-100" />
    </button>
  );
}
